import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { GlobalStyles, Theme, ThemeProvider } from '@mui/material';
import { alpha, createTheme, darken } from '@mui/material/styles';
import { DarkTheme, LightTheme, ThemeModeEnum } from '@/constants';
import {
  useGetUnauthPrivateLabelByAdvisorGroupIdQuery,
  useGetUnauthPrivateLabelQuery,
} from '@/store/authApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAdvisorGroupColorsQuery } from '@/store/colorsApi';
import { deriveAdvisorGroupId } from '@/utils/deriveAdvisorGroupId';
import {
  clearSessionBrandingAdvisorGroupId,
  getPreAuthBrandingAdvisorGroupId,
} from '@/utils/preAuthBrandingAdvisorGroupId';
import {
  getLastSignedInAdvisorGroupId,
  setLastSignedInAdvisorGroupId,
} from '@/utils/lastSignedInAdvisorGroupId';

interface ThemeContextInterface {
  themeMode: ThemeModeEnum;
  themeUnderlyingPreference: ThemeModeEnum;
  switchThemeMode: (mode: ThemeModeEnum) => void;
  setThemeUnderlyingPreference: (mode: ThemeModeEnum) => void;
  brandColors: {
    primary: string | null;
    secondary: string | null;
    hasPrimaryAndSecondary: boolean;
  };
}

const defaultContext: ThemeContextInterface = {
  themeMode: ThemeModeEnum.light,
  themeUnderlyingPreference: ThemeModeEnum.light,
  switchThemeMode: () => {},
  setThemeUnderlyingPreference: () => {},
  brandColors: {
    primary: null,
    secondary: null,
    hasPrimaryAndSecondary: false,
  },
};

export const ThemeContext =
  createContext<ThemeContextInterface>(defaultContext);

function normalizeCssColor(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return null;
  }

  // Accept common hex formats even when '#' is missing.
  if (/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(trimmed)) {
    return `#${trimmed}`;
  }

  return trimmed;
}

export const ThemeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeModeEnum>(
    ThemeModeEnum.light,
  );
  const [themeUnderlyingPreference, setThemeUnderlyingPreference] =
    useState<ThemeModeEnum>(ThemeModeEnum.light);
  const [theme, setTheme] = useState<Theme>(LightTheme);

  const authLoaded = useAuthIsLoaded();
  const userInfo = useSelector((state: WARootState) => state.appView.UserInfo);

  const userAdvisorGroupId = useMemo(() => {
    return deriveAdvisorGroupId(userInfo);
  }, [userInfo]);

  const preAuthAdvisorGroupId = useMemo(
    () => getPreAuthBrandingAdvisorGroupId() ?? getLastSignedInAdvisorGroupId(),
    [],
  );

  const brandingAdvisorGroupId =
    typeof userAdvisorGroupId === 'number'
      ? userAdvisorGroupId
      : preAuthAdvisorGroupId;

  const { data: unauthPrivateLabel } = useGetUnauthPrivateLabelQuery();
  const { data: unauthPrivateLabelByAdvisorGroupId } =
    useGetUnauthPrivateLabelByAdvisorGroupIdQuery(
      typeof brandingAdvisorGroupId === 'number'
        ? { advisorGroupId: brandingAdvisorGroupId }
        : skipToken,
    );

  const { data: advisorGroupGraphColors } = useGetAdvisorGroupColorsQuery(
    authLoaded && typeof userAdvisorGroupId === 'number'
      ? { advisorGroupId: userAdvisorGroupId }
      : skipToken,
  );

  const loggedAuthPrivateLabelRef = useRef(false);
  const loggedAdvisorGroupGraphColorsRef = useRef(false);

  useEffect(() => {
    if (!authLoaded) {
      return;
    }
    // Helpful breadcrumb to confirm the branch is executing even if the query is skipped.
    console.log(
      'Branding (post-auth): derived advisorGroupId =',
      userAdvisorGroupId,
    );
  }, [userAdvisorGroupId, authLoaded]);

  useEffect(() => {
    // Once we have the signed-in advisorGroupId, clear any pre-auth session override so
    // subsequent sign-outs fall back to host/origin branding unless a URL param is present.
    if (!authLoaded) return;
    if (typeof userAdvisorGroupId !== 'number') return;

    // Persist for post-logout / session-expired redirects (Capacitor).
    setLastSignedInAdvisorGroupId(userAdvisorGroupId);
    clearSessionBrandingAdvisorGroupId();
  }, [authLoaded, userAdvisorGroupId]);

  useEffect(() => {
    if (!authLoaded || typeof userAdvisorGroupId !== 'number') {
      return;
    }
    if (!unauthPrivateLabelByAdvisorGroupId) {
      return;
    }
    if (loggedAuthPrivateLabelRef.current) {
      return;
    }
    loggedAuthPrivateLabelRef.current = true;
    console.log('UnauthPrivateLabel (post-auth, by advisorGroupId):', {
      advisorGroupId: userAdvisorGroupId,
      unauthPrivateLabelByAdvisorGroupId,
    });
  }, [userAdvisorGroupId, authLoaded, unauthPrivateLabelByAdvisorGroupId]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    if (!authLoaded || typeof userAdvisorGroupId !== 'number') {
      return;
    }
    if (!advisorGroupGraphColors) {
      return;
    }
    if (loggedAdvisorGroupGraphColorsRef.current) {
      return;
    }
    loggedAdvisorGroupGraphColorsRef.current = true;
    console.log('AdvisorGroup graph colors:', {
      advisorGroupId: userAdvisorGroupId,
      advisorGroupGraphColors,
    });
  }, [advisorGroupGraphColors, userAdvisorGroupId, authLoaded]);

  const setThemeBasedOnPreference = (prefersDark: boolean) => {
    setThemeMode(prefersDark ? ThemeModeEnum.dark : ThemeModeEnum.light);
    setTheme(prefersDark ? DarkTheme : LightTheme);
  };

  useEffect(() => {
    const themePreference = localStorage.getItem(
      'themeMode',
    ) as ThemeModeEnum | null;
    if (
      themePreference === ThemeModeEnum.dark ||
      themePreference === ThemeModeEnum.light
    ) {
      setThemeMode(themePreference);
      setThemeUnderlyingPreference(themePreference);
    } else {
      setThemeUnderlyingPreference(ThemeModeEnum.system);
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setThemeBasedOnPreference(mediaQuery.matches);

      const handleChange = (e: MediaQueryListEvent) => {
        setThemeBasedOnPreference(e.matches);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, []);

  useEffect(() => {
    switch (themeUnderlyingPreference) {
      case ThemeModeEnum.light:
        setTheme(LightTheme);
        break;
      case ThemeModeEnum.dark:
        setTheme(DarkTheme);
        break;
      case ThemeModeEnum.system: {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setThemeBasedOnPreference(mediaQuery.matches);
        break;
      }
      default:
        setTheme(LightTheme);
        break;
    }
  }, [themeUnderlyingPreference, themeMode]);

  const brandColors = useMemo(() => {
    const hasBrandingValues = (
      label: typeof unauthPrivateLabelByAdvisorGroupId,
    ): boolean => {
      if (!label) return false;
      return Boolean(
        label.PrimaryColor ||
        label.PrimaryColorHex ||
        label.SecondaryColor ||
        label.SecondaryColorHex ||
        label.ImagePath ||
        label.MobileImagePath ||
        label.MobileLogoPath ||
        label.IconPath ||
        label.LogoHeight ||
        label.AboutUrl,
      );
    };

    const shouldPreferAuthPrivateLabel =
      unauthPrivateLabelByAdvisorGroupId &&
      (unauthPrivateLabelByAdvisorGroupId.Found === true ||
        unauthPrivateLabelByAdvisorGroupId.AdvisorGroupId ===
          brandingAdvisorGroupId ||
        hasBrandingValues(unauthPrivateLabelByAdvisorGroupId));

    const effectiveLabel = shouldPreferAuthPrivateLabel
      ? unauthPrivateLabelByAdvisorGroupId
      : unauthPrivateLabel;

    const primary =
      normalizeCssColor(effectiveLabel?.PrimaryColor) ??
      normalizeCssColor(effectiveLabel?.PrimaryColorHex);
    const secondary =
      normalizeCssColor(effectiveLabel?.SecondaryColor) ??
      normalizeCssColor(effectiveLabel?.SecondaryColorHex);

    return {
      primary,
      secondary,
      hasPrimaryAndSecondary: Boolean(primary && secondary),
    };
  }, [
    brandingAdvisorGroupId,
    unauthPrivateLabel,
    unauthPrivateLabelByAdvisorGroupId,
  ]);

  const themedWithPrivateLabel = useMemo(() => {
    const effectivePrimary = brandColors.primary;
    const effectiveSecondary = brandColors.secondary;

    if (!effectivePrimary && !effectiveSecondary) {
      return theme;
    }

    return createTheme(theme, {
      palette: {
        ...(effectivePrimary
          ? {
              primary: {
                main: effectivePrimary,
                dark: darken(effectivePrimary, 0.15),
              },
              custom: {
                overlay: {
                  primary: alpha(effectivePrimary, 0.04),
                  primaryHover: alpha(effectivePrimary, 0.08),
                  selected: alpha(effectivePrimary, 0.08),
                  selectedHover: alpha(effectivePrimary, 0.12),
                },
              },
            }
          : {}),
        ...(effectiveSecondary
          ? {
              secondary: {
                main: effectiveSecondary,
                dark: darken(effectiveSecondary, 0.15),
              },
            }
          : {}),
      },
    });
  }, [brandColors.primary, brandColors.secondary, theme]);

  const _setThemeModeToPref = (mode: ThemeModeEnum) =>
    localStorage.setItem('themeMode', mode);

  const switchThemeMode = (mode: ThemeModeEnum) => {
    _setThemeModeToPref(mode);
    setThemeUnderlyingPreference(mode);
  };

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        themeUnderlyingPreference,
        switchThemeMode,
        setThemeUnderlyingPreference,
        brandColors,
      }}
    >
      <ThemeProvider theme={themedWithPrivateLabel}>
        <GlobalStyles
          styles={(muiTheme) => ({
            a: {
              textDecorationColor: muiTheme.palette.primary.main,
            },
          })}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
